import * as React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { ArrowLeft20Regular } from "@fluentui/react-icons";
import { RisikoKfzStepForm } from "./RisikoKfzStepForm";
import { RisikoImmobilieStepForm } from "./RisikoImmobilieStepForm";
import BusinessAssetsDialog, { type BusinessAssetsRiskData } from "../components/BusinessAssetsDialog";
import NewRiskDialog from "../components/NewRiskDialog";
import PageModeControls from "../components/windowManager/PageModeControls";
import { useIsPageChrome } from "../components/windowManager/pageChrome";
import { RisikoMaschinenStepForm } from "./RisikoMaschinenStepForm";
import { RisikoLiquiditaetStepForm } from "./RisikoLiquiditaetStepForm";
import {
  liquiditaetSchema,
  ertragsausfallSchema,
} from "../components/risikoLiquiditaetStep/schema";
import { cyberSchema } from "../components/risikoCyberStep/schema";
import { RisikoUmweltStepForm } from "./RisikoUmweltStepForm";
import { RisikoTransportStepForm } from "./RisikoTransportStepForm";
import { apiUrl } from "../utils/api";
import { characteristicsToAnswers } from "../utils/riskCharacteristics";
import { parseStructuredAnswers } from "../components/risikoImmobilieStep/structuredAnswers";
import { type UploadMode } from "../components/risikoImmobilieStep/uploadBuffer";
import { baueUnterRisiken } from "../components/risikoImmobilieStep/unterrisiken";
import { speichereUnterRisiken } from "../components/risikoImmobilieStep/unterrisikenSpeichern";
import {
  ladeVorgemerkteHoch,
  zieleFuerGebaeude,
  type UploadFehler,
} from "../components/risikoImmobilieStep/uploadRunner";
import { BRAND } from "../components/risikoanalyseStep/tokens";

// ---- Tile catalog --------------------------------------------------------

type RiskKey =
  | "betriebsvermoegen"
  | "machine"
  | "waren"
  | "elektronik"
  | "betriebSonstige"
  | "immobilie"
  | "kfz"
  | "umwelt"
  | "transport"
  | "menschen"
  | "tobJobber"
  | "liquiditaet"
  | "ertragsausfall"
  | "cyber"
  | "photovoltaik"
  | "sonstiges";

// Inline icons in the same style as ContactRisksPanel (white-stroke).
const ic = (children: JSX.Element) => (
  <svg
    viewBox="0 0 36 36"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: "100%", height: "100%", display: "block" }}
  >
    {children}
  </svg>
);

const TileIcons = {
  chart: () =>
    ic(
      <>
        <line x1="5" y1="30" x2="31" y2="30" />
        <rect x="8" y="20" width="4" height="10" />
        <rect x="16" y="14" width="4" height="16" />
        <rect x="24" y="9" width="4" height="21" />
      </>,
    ),
  car: () =>
    ic(
      <>
        <path d="M6 22l3-8a3 3 0 0 1 2.8-2h12.4a3 3 0 0 1 2.8 2l3 8" />
        <path d="M4 22h28v6H4z" />
        <circle cx="11" cy="26" r="2" />
        <circle cx="25" cy="26" r="2" />
      </>,
    ),
  house: () =>
    ic(
      <>
        <path d="M5 18l13-11 13 11" />
        <path d="M8 16v14h20V16" />
        <rect x="15" y="20" width="6" height="10" />
      </>,
    ),
  factory: () =>
    ic(
      <>
        <path d="M5 30V16l7 4v-4l7 4V12l9 6v12z" />
        <line x1="11" y1="26" x2="11" y2="28" />
        <line x1="18" y1="26" x2="18" y2="28" />
        <line x1="25" y1="26" x2="25" y2="28" />
      </>,
    ),
  briefcase: () =>
    ic(
      <>
        <rect x="5" y="11" width="26" height="18" rx="2" />
        <path d="M13 11V8a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v3" />
        <line x1="5" y1="19" x2="31" y2="19" />
      </>,
    ),
  shield: () =>
    ic(
      <>
        <path d="M18 4l11 4v8c0 7-5 13-11 16-6-3-11-9-11-16V8z" />
        <path d="M13 18l4 4 7-8" />
      </>,
    ),
  tree: () =>
    ic(
      <>
        <path d="M18 4l7 9h-4l5 7h-4l4 6H10l4-6h-4l5-7H11z" />
        <line x1="18" y1="26" x2="18" y2="32" />
        <line x1="14" y1="32" x2="22" y2="32" />
      </>,
    ),
  truck: () =>
    ic(
      <>
        <rect x="3" y="11" width="17" height="13" />
        <path d="M20 14h7l5 5v5H20z" />
        <circle cx="10" cy="27" r="2.5" />
        <circle cx="26" cy="27" r="2.5" />
      </>,
    ),
  people: () =>
    ic(
      <>
        <circle cx="13" cy="13" r="4" />
        <circle cx="24" cy="14" r="3" />
        <path d="M5 28c0-4 3.5-7 8-7s8 3 8 7" />
        <path d="M21 28c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      </>,
    ),
  euro: () =>
    ic(
      <>
        <circle cx="18" cy="18" r="13" />
        <path d="M25 11a9 9 0 1 0 0 14" />
        <line x1="9" y1="16" x2="22" y2="16" />
        <line x1="9" y1="20" x2="22" y2="20" />
      </>,
    ),
  trendDown: () =>
    ic(
      <>
        <polyline points="5,12 13,18 19,14 31,24" />
        <polyline points="25,24 31,24 31,18" />
      </>,
    ),
  cyber: () =>
    ic(
      <>
        <path d="M18 4l11 4v8c0 7-5 13-11 16-6-3-11-9-11-16V8z" />
        <circle cx="18" cy="17" r="3" />
        <path d="M18 20v5" />
      </>,
    ),
};

type TileSpec = {
  key: RiskKey;
  label: string;
  hint?: string;
  color: string;
  Icon: () => JSX.Element;
  comingSoon?: boolean;
  subRisks?: string[];
};

const SONSTIGES_HINT = [
  "Risiko Tiere",
  "Risiko aus Rechtsstreitigkeiten",
  "Kann betreffen Risiko Kfz, Risiko Immobilien, Risiko Mitarbeiter",
  "Risiko Radioaktive Geräte o. Stoffe, Röntgenstrahlen",
  "Risiko Firmenfahrräder, E-Bikes",
  "Risiko Kunstgegenstände, wertvolle Objekte",
  "Risiko Luftfahrzeuge",
  "Risiko Fracht-, Passagier-, Spezialschiffe, Kutter, Boote etc.",
  "Risiko Montagen",
  "Risiko Veranstaltungen",
  "Risiko Geschäfts- u. Dienstreisen",
  "Risiko Patente, Marken",
  "Risiko Gewinnspiele",
  "Risiko Schlüsselverlust",
].join("\n");

const TILES: TileSpec[] = [
  { key: "betriebsvermoegen", label: "Sachwerte", color: "#7c5a3a", Icon: TileIcons.briefcase },
  { key: "machine", label: "Maschinen", color: "#57534e", Icon: TileIcons.factory },
  { key: "immobilie", label: "Immobilien", color: "#1e40af", Icon: TileIcons.house },
  { key: "kfz", label: "KFZ", color: "#b91c1c", Icon: TileIcons.car },
  { key: "umwelt", label: "Umwelt", color: "#15803d", Icon: TileIcons.tree },
  { key: "transport", label: "Transport, Messen u. Ausstellungen", color: "#c2410c", Icon: TileIcons.truck },
  { key: "menschen", label: "Menschen im Unternehmen", color: "#db2777", Icon: TileIcons.people, comingSoon: true },
  { key: "tobJobber", label: "Tob-Jobber", color: "#7c3aed", Icon: TileIcons.people, comingSoon: true },
  { key: "liquiditaet", label: "Liquidität", color: "#ca8a04", Icon: TileIcons.euro },
  { key: "ertragsausfall", label: "Ertragsausfall", color: "#a16207", Icon: TileIcons.trendDown },
  { key: "cyber", label: "Cyber", color: "#0f172a", Icon: TileIcons.cyber },
  { key: "photovoltaik", label: "Photovoltaik", color: "#0d9488", Icon: TileIcons.briefcase, comingSoon: true },
  { key: "sonstiges", label: "Sonstiges", color: "#475569", Icon: TileIcons.briefcase, hint: SONSTIGES_HINT, comingSoon: true },
];

// ---- Styles --------------------------------------------------------------

const useStyles = makeStyles({
  page: {
    paddingTop: "24px",
    paddingBottom: "32px",
    paddingLeft: "16px",
    paddingRight: "16px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    backgroundColor: BRAND.bg,
    // Topbar-Höhe abziehen — `--topbar-h` (= 64px + obere Safe-Area) als einzige
    // Quelle, Fallback 64px ohne Notch/viewport-fit=cover.
    minHeight: "calc(100vh - var(--topbar-h, 64px))",
    "@media (min-width: 768px)": {
      paddingTop: "40px",
      paddingLeft: "32px",
      paddingRight: "32px",
    },
  },
  /**
   * VOLLFLÄCHE — nur für das Immobilien-Formular (Auftraggeber, 19.08.2026:
   * „volle Höhe und Breite, wir haben beim Scrollen sonst Probleme").
   *
   * Die Standard-Schale oben ist auf eine Dokumentseite ausgelegt: Innenabstand
   * ringsum und `min-height: 100vh − Topbar`. Beides ist im Fenster falsch —
   * der Fenster-Body ist NICHT der Viewport, die Seite war dort also selbst
   * dann höher als der Rahmen, wenn kaum Inhalt da war (Dauer-Scrollbalken),
   * und die Höhe blieb für die Kinder unbestimmt.
   *
   * Diese Variante macht die Seite stattdessen zu einer definiten Flex-Spalte
   * in der Höhe des Rahmens. Erst dadurch kann das Formular darin seine eigene
   * Höhenkette aufspannen und `main` zum einzigen Scroll-Container machen.
   *
   * Der Innenabstand MUSS auch in der Media-Query zurückgenommen werden:
   * Griffel erzeugt je Regelkontext eine eigene atomare Klasse — ein
   * `paddingTop: 0` auf oberster Ebene überschreibt das `40px` aus
   * `@media (min-width: 768px)` nicht.
   */
  pageVollflaeche: {
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    ...shorthands.padding("0"),
    "@media (min-width: 768px)": {
      ...shorthands.padding("0"),
    },
  },
  topRow: {
    maxWidth: "1200px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    alignItems: "center",
    columnGap: "16px",
    marginBottom: "16px",
  },
  /**
   * VOLLSEITE: die Kopfzeile bleibt beim Scrollen stehen. Sie ersetzt dort die
   * Fenster-Titelleiste — und die lag außerhalb des Scrollers, war also immer
   * erreichbar. Ohne `sticky` wären „Zurück", „Als Fenster" und „Minimieren"
   * nach dem ersten Scrollen weg. Scroll-Container ist der Fenster-Body
   * (FloatingWindow `styles.body`, overflow:auto) — der nächste scrollende
   * Vorfahr, an dem `top: 0` greift.
   */
  topRowSticky: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    backgroundColor: BRAND.bg,
    paddingTop: "8px",
    paddingBottom: "8px",
  },
  /** Drückt die Vollseiten-Bedienelemente nach rechts. */
  topRowSpacer: {
    marginLeft: "auto",
  },
  title: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.8rem",
    fontWeight: 600,
    color: BRAND.ink,
  },
  ghostBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "12px",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    ":hover": { backgroundColor: BRAND.white },
  },
  card: {
    maxWidth: "1200px",
    marginLeft: "auto",
    marginRight: "auto",
    backgroundColor: BRAND.white,
    paddingTop: "28px",
    paddingBottom: "28px",
    paddingLeft: "24px",
    paddingRight: "24px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("14px"),
    boxShadow: "0 12px 32px rgba(45,20,43,0.08)",
  },
  cardHead: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    marginBottom: "20px",
  },
  cardEyebrow: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  cardTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.4rem",
    color: BRAND.ink,
    fontWeight: 600,
  },
  goldLine: {
    height: "3px",
    backgroundColor: BRAND.gold,
    width: "60px",
    ...shorthands.borderRadius("999px"),
    marginBottom: "20px",
  },
  tileGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: "14px",
  },
  tile: {
    appearance: "none",
    cursor: "pointer",
    color: BRAND.white,
    backgroundColor: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    textAlign: "left",
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    minHeight: "150px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
    ...shorthands.border("0"),
    ...shorthands.borderRadius("12px"),
    transitionProperty: "transform, box-shadow",
    transitionDuration: "150ms",
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 14px 26px rgba(45,20,43,0.18)",
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.55,
      ":hover": { transform: "none", boxShadow: "none" },
    },
  },
  tileTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    columnGap: "8px",
  },
  tileIconWrap: {
    width: "40px",
    height: "40px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "9px",
    paddingRight: "9px",
    backgroundColor: "rgba(255,255,255,0.14)",
    color: BRAND.white,
    flexShrink: 0,
    ...shorthands.borderRadius("10px"),
  },
  tileBereich: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "6px",
    paddingRight: "8px",
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: BRAND.white,
    backgroundColor: "rgba(0,0,0,0.18)",
    ...shorthands.borderRadius("999px"),
  },
  tileBereichDot: {
    width: "8px",
    height: "8px",
    backgroundColor: BRAND.white,
    ...shorthands.borderRadius("999px"),
  },
  tileBody: { marginTop: "auto" },
  tileLabel: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    fontWeight: 600,
    lineHeight: 1.2,
    color: BRAND.white,
  },
  tileHint: {
    fontSize: "0.78rem",
    color: "rgba(255,255,255,0.78)",
    marginTop: "4px",
  },
  tileWide: {
    "@media (min-width: 760px)": { gridColumn: "span 2" },
  },
  subRiskFooter: {
    marginTop: "12px",
    paddingTop: "10px",
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    columnGap: "6px",
    rowGap: "6px",
    ...shorthands.borderTop("1px", "solid", "rgba(255,255,255,0.28)"),
  },
  subRiskBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "4px",
    paddingRight: "4px",
    fontSize: "0.7rem",
    fontWeight: 600,
    lineHeight: 1.15,
    color: BRAND.white,
    backgroundColor: "rgba(255,255,255,0.14)",
    ...shorthands.borderRadius("6px"),
  },
  tileSoonBadge: {
    position: "absolute",
    top: "10px",
    right: "10px",
    fontSize: "0.62rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    backgroundColor: BRAND.gold,
    color: BRAND.ink,
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "6px",
    paddingRight: "8px",
    ...shorthands.borderRadius("999px"),
  },
  formContainer: {
    maxWidth: "1200px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  /**
   * Passend zu `pageVollflaeche`: kein 1200px-Deckel und keine Zentrierung
   * mehr, sondern die volle Fläche als weitergereichte Flex-Spalte.
   *
   * Der Deckel war zusätzlich tückisch, weil `useTocLayout` das
   * Inhaltsverzeichnis ab einer GEMESSENEN Containerbreite von 1200px dauerhaft
   * einblendet — die Schwelle lag also exakt auf der Kappungsgrenze, und ein
   * paar Pixel Innenabstand entschieden über „Verzeichnis oder Hamburger".
   *
   * Die Lesespalte bleibt trotzdem begrenzt: dafür sorgt das Formular selbst
   * (`RisikoImmobilieStepForm`, Stil `inner`).
   */
  formContainerVollflaeche: {
    maxWidth: "none",
    marginLeft: 0,
    marginRight: 0,
    flexGrow: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  toast: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    backgroundColor: BRAND.ink,
    color: BRAND.white,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.borderRadius("8px"),
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    boxShadow: "0 12px 28px rgba(0,0,0,0.25)",
    zIndex: 1000,
  },
  toastSuccess: { backgroundColor: "#0e7c3a" },
  toastError: { backgroundColor: "#b91c1c" },
  /*
    BLEIBENDER Hinweis — bewusst kein Toast. `showToast` löscht sich nach
    2400 ms und `navigateAfterSave()` schließt das Fenster nach 600 ms; eine
    Upload-Teilstörung wäre damit faktisch unlesbar. Dieser Kasten bleibt
    stehen, bis der Nutzer ihn schließt.
  */
  uploadHinweis: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    maxWidth: "420px",
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "16px",
    paddingRight: "16px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    boxShadow: "0 12px 28px rgba(0,0,0,0.25)",
    zIndex: 1001,
    ...shorthands.borderRadius("8px"),
    ...shorthands.borderLeft("4px", "solid", "#b91c1c"),
  },
  uploadHinweisTitel: {
    fontWeight: 700,
  },
  uploadHinweisListe: {
    marginTop: 0,
    marginBottom: 0,
    paddingLeft: "18px",
    fontSize: "0.82rem",
    color: "#5d5664",
  },
  uploadHinweisZeile: {
    display: "flex",
    columnGap: "8px",
    justifyContent: "flex-end",
  },
  uploadHinweisBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.purple,
    backgroundImage: "none",
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    fontWeight: 600,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("0"),
    ...shorthands.borderRadius("6px"),
  },
  uploadHinweisBtnGhost: {
    backgroundColor: "transparent",
    color: BRAND.purple,
    ...shorthands.border("1px", "solid", BRAND.purple),
  },
  uploadFortschritt: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    backgroundColor: BRAND.ink,
    color: BRAND.white,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "16px",
    paddingRight: "16px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    boxShadow: "0 12px 28px rgba(0,0,0,0.25)",
    zIndex: 1000,
    ...shorthands.borderRadius("8px"),
  },
});

// ---- Component -----------------------------------------------------------

export type NewRiskPageEmbedded = {
  /** ID der Fenster-Instanz — für die Vollseiten-Bedienelemente. */
  windowId?: string;
  riskType?: string;
  kontaktId?: number | null;
  /**
   * Name des Kunden, für den erfasst wird. Der Fenster-Adapter kennt ihn
   * ohnehin (Fenstertitel); das Immobilien-Formular zeigt ihn seit dem Wegfall
   * der Kopfzeile in seiner Kontextzeile.
   */
  kontaktName?: string | null;
  attachToContractId?: number | null;
  /**
   * Edit-Mode: wenn gesetzt, lädt die Page das bestehende Risk-Object und
   * füllt die Step-Form mit den vorhandenen Werten vor. Speichern macht
   * dann ein PUT statt POST.
   */
  editRiskId?: number | null;
  onSuccess?: () => void;
  /**
   * Wird vom Window-Adapter geliefert. Sobald der User Kennzeichen / Adresse
   * tippt, ruft die Page diesen Callback mit dem Suffix-String auf — der
   * Window-Manager hängt den Suffix an den Basistitel an.
   */
  onTitleSuffix?: (suffix: string | null) => void;
  /**
   * Meldet ungespeicherte Eingaben an den Window-Adapter (→ Close-Rückfrage).
   * Wird beim reinen Öffnen NICHT gefeuert (Skip-First in den Step-Formularen).
   */
  onDirtyChange?: (dirty: boolean) => void;
  /** „Zurück": schließt die Vollseite/das Fenster statt zu navigieren. */
  onClose?: () => void;
  /**
   * Typwechsel im Picker: ersetzt die Instanz (close + open mit neuer ID),
   * statt ein zweites Fenster zu öffnen.
   */
  onReplace?: (
    riskType: string,
    contactId: number | null,
    contactName: string | null,
  ) => void;
};

type NewRiskPageProps = {
  embedded?: NewRiskPageEmbedded;
};

const NewRiskPage: React.FC<NewRiskPageProps> = ({ embedded } = {}) => {
  const s = useStyles();
  // Nur in der Vollseite klebt die Kopfzeile — im Fenster übernimmt die
  // Titelleiste, auf der Route gibt es keinen eigenen Scroll-Container.
  const isPageChrome = useIsPageChrome();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isEmbedded = Boolean(embedded);
  const attachToContractId = (() => {
    if (isEmbedded) {
      const n = Number(embedded?.attachToContractId);
      return Number.isFinite(n) && n > 0 ? n : null;
    }
    const raw = searchParams.get("attachToContract");
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  // Risk type may be pre-selected via navigation state from NewRiskDialog.
  const stateRiskType = isEmbedded
    ? (typeof embedded?.riskType === "string" ? (embedded.riskType as RiskKey) : null)
    : (typeof location.state?.riskType === "string"
      ? (location.state.riskType as RiskKey)
      : null);
  const stateKontaktId = (() => {
    if (isEmbedded) {
      const n = Number(embedded?.kontaktId);
      return Number.isFinite(n) && n > 0 ? n : null;
    }
    const n = Number(location.state?.kontaktId);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  const [selected, setSelected] = React.useState<RiskKey | null>(stateRiskType);
  const [businessAssetsRisk, setBusinessAssetsRisk] =
    React.useState<BusinessAssetsRiskData | null>(null);

  // If we landed here without a selection AND without a contact context, show
  // the unified dialog so the user picks both a type and a contact first.
  const [pickerDialogOpen, setPickerDialogOpen] = React.useState<boolean>(
    !stateRiskType && stateKontaktId == null,
  );

  // Sync `selected` with navigation state — the dialog calls `navigate("/risks/new", { state })`
  // while we're already on this route, so the initial useState value won't update on its own.
  React.useEffect(() => {
    if (stateRiskType && stateRiskType !== selected) {
      setSelected(stateRiskType);
      setPickerDialogOpen(false);
    }
  }, [stateRiskType, selected]);

  // ── Edit-Mode ─────────────────────────────────────────────────────────
  // Wenn embedded.editRiskId gesetzt ist: GET /risk-objects/:id und Prefill
  // bauen, damit die Step-Form mit den existierenden Werten startet.
  const editRiskId = isEmbedded
    ? (Number.isFinite(Number(embedded?.editRiskId)) && Number(embedded?.editRiskId) > 0
        ? Number(embedded?.editRiskId)
        : null)
    : null;
  const [editLoading, setEditLoading] = React.useState<boolean>(Boolean(editRiskId));
  const [editError, setEditError] = React.useState<string | null>(null);
  const [kfzInitialAnswers, setKfzInitialAnswers] =
    React.useState<Record<string, string> | null>(null);
  const [immoInitialGlobalAnswers, setImmoInitialGlobalAnswers] =
    React.useState<Record<string, unknown> | null>(null);
  const [immoInitialGebaeude, setImmoInitialGebaeude] =
    React.useState<Array<{ id: string; label: string; answers: Record<string, unknown> }> | null>(null);

  React.useEffect(() => {
    if (!editRiskId) return;
    setEditLoading(true);
    setEditError(null);
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(apiUrl(`risk-objects/${editRiskId}`));
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const risk = await res.json();
        if (aborted) return;
        const flat = characteristicsToAnswers(risk.characteristics);
        const rt = String(risk.riskType ?? "").toLowerCase();
        if (rt.includes("kfz")) {
          setSelected("kfz");
          setKfzInitialAnswers(flat);
          setPickerDialogOpen(false);
        } else if (rt.includes("immo")) {
          setSelected("immobilie");
          // Strukturierte Merkmale liegen als JSON-String in den Characteristics
          // (Speicherpfad nutzt JSON.stringify) — vor der Übergabe ans Formular
          // zurückparsen, sonst kracht z. B. `einheiten.map(...)` im Edit.
          const immoFlat = parseStructuredAnswers(flat);
          // Adresse-Keys leben global, Rest am Gebäude — wir kopieren alles in
          // beide Bags (Form merged beim Submit eh).
          setImmoInitialGlobalAnswers(immoFlat);
          setImmoInitialGebaeude([
            {
              id: "g-edit",
              label: String(risk.name ?? "Hauptgebäude"),
              answers: immoFlat,
            },
          ]);
          setPickerDialogOpen(false);
        } else {
          // Andere Typen: noch nicht unterstützt — Fehler-Banner.
          setEditError(`Risiko-Typ "${risk.riskType}" wird im Edit-Mode noch nicht unterstützt.`);
        }
      } catch (err) {
        if (!aborted) setEditError(err instanceof Error ? err.message : "Laden fehlgeschlagen");
      } finally {
        if (!aborted) setEditLoading(false);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [editRiskId]);

  const [toast, setToast] = React.useState<{
    kind: "success" | "error";
    message: string;
  } | null>(null);
  const showToast = (kind: "success" | "error", message: string) => {
    setToast({ kind, message });
    setTimeout(() => setToast(null), 2400);
  };

  /** „Dateien werden hochgeladen … 2/5" — nur während des Nachlaufs. */
  const [uploadFortschritt, setUploadFortschritt] = React.useState<
    { erledigt: number; gesamt: number } | null
  >(null);
  /**
   * Teilfehler beim Datei-Nachlauf. BLEIBT stehen (kein Toast) und hält das
   * Fenster offen — sonst verschwände die einzige Spur eines fehlgeschlagenen
   * Uploads mitsamt dem Fenster, bevor sie jemand lesen kann.
   */
  const [uploadProblem, setUploadProblem] = React.useState<{
    fehler: UploadFehler[];
    erfolgreich: number;
    riskObjectId: number | null;
  } | null>(null);
  /**
   * Die Immobilie ist gespeichert — ab hier darf NICHT erneut abgesendet
   * werden. Das Fenster bleibt bei einer Upload-Teilstörung bewusst offen;
   * ohne diese Sperre wäre der Speichern-Knopf wieder aktiv (die Step-Form
   * setzt `submitting` im `finally` zurück) und ein zweiter Klick legte alle
   * Gebäude ein zweites Mal an.
   */
  const [immoGespeichert, setImmoGespeichert] = React.useState(false);
  /**
   * Nach dem Speichern gibt es keine ungespeicherten Eingaben mehr — auch
   * dann nicht, wenn wegen einer Upload-Störung noch ein Hinweis steht. Sonst
   * fragte der Fenster-Guard beim Schließen nach „ungespeicherten Änderungen".
   */
  const immoGespeichertMerken = React.useCallback(() => {
    setImmoGespeichert(true);
    embedded?.onDirtyChange?.(false);
  }, [embedded]);

  /**
   * Upload-Verhalten der Immobilien-Maske: im Edit-Modus liegt die
   * `riskObjectId` bereits vor (Sofort-Upload), bei der Neuanlage entsteht sie
   * erst im Submit (Puffer). Beides nur eingebettet/angemeldet — die
   * öffentlichen Wege bekommen die Prop gar nicht erst.
   */
  const immoUploadMode: UploadMode = React.useMemo(() => {
    if (editRiskId) {
      return { mode: "immediate", riskObjectId: editRiskId, contactId: stateKontaktId };
    }
    return { mode: "buffer" };
  }, [editRiskId, stateKontaktId]);

  const navigateAfterSave = () => {
    if (isEmbedded) {
      setTimeout(() => embedded?.onSuccess?.(), 600);
      return;
    }
    const kontaktId = Number(location.state?.kontaktId);
    setTimeout(() => {
      if (Number.isFinite(kontaktId) && kontaktId > 0) {
        navigate(`/contacts/${kontaktId}`, {
          state: { k: location.state?.k, defaultTab: "risiken" },
        });
      } else {
        navigate("/risks");
      }
    }, 600);
  };

  const handleClose = React.useCallback(() => {
    // Eingebettet („Vollseite"/Fenster) schließt „Zurück" die Instanz — der
    // Adapter fragt bei ungespeicherten Eingaben vorher nach. Navigieren würde
    // die Erfassung sonst unsichtbar im Hintergrund stehen lassen.
    if (isEmbedded) {
      embedded?.onClose?.();
      return;
    }
    const kontaktId = Number(location.state?.kontaktId);
    if (Number.isFinite(kontaktId) && kontaktId > 0) {
      navigate(`/contacts/${kontaktId}`, {
        state: { k: location.state?.k, defaultTab: "risiken" },
      });
    } else {
      navigate("/risks");
    }
    // In `useCallback`, weil der Handler seit 19.08.2026 auch als Prop im
    // Immobilien-Formular hängt (Kontextzeile → „Schließen").
  }, [isEmbedded, embedded, location, navigate]);

  const saveKfzRiskObject = React.useCallback(
    async (data: Record<string, any>): Promise<{ id: number | null }> => {
      const classLabel =
        typeof data.vehicleClass === "string" ? data.vehicleClass.trim() : "";
      const plate =
        typeof data.licensePlate === "string" ? data.licensePlate.trim() : "";
      const ort = plate.length > 0 ? `${classLabel || "KFZ"}: ${plate}` : classLabel || "KFZ";
      // stateKontaktId berücksichtigt den embedded-(Fenster-)Modus, in dem
      // location.state leer ist — sonst fiele contactID auf null (verwaistes Risiko).
      const kontaktId = stateKontaktId;
      const vertragId = Number(location.state?.vertragId);

      const characteristics = Object.entries(data)
        .filter(
          ([, value]) =>
            value !== undefined && value !== null && String(value).trim() !== "",
        )
        .map(([nameBezeichnung, value]) => ({
          nameBezeichnung,
          riskCharacteristicValue: String(value),
        }));

      // Wenn ein `?attachToContract`-Param gesetzt ist, hängen wir das neue
      // Risiko hinterher per POST /contracts/:id/risk-objects an. `contractID`
      // im RiskObject-Payload würde die FK direkt setzen, aber wir wollen den
      // Many-to-Many-Eintrag (RiskObjectContract) ebenfalls schreiben.
      // Im Edit-Mode (editRiskId gesetzt): PUT /risk-objects/:id statt POST.
      const isEdit = Boolean(editRiskId);
      const url = isEdit
        ? apiUrl(`risk-objects/${editRiskId}`)
        : apiUrl("risk-objects");
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contractID:
            attachToContractId ??
            (Number.isFinite(vertragId) && vertragId > 0 ? vertragId : null),
          contactID: kontaktId,
          riskType: "KFZ",
          name: ort,
          characteristics,
        }),
      });
      if (!res.ok) {
        const message = await res.text();
        throw new Error(message || "KFZ speichern fehlgeschlagen");
      }
      let createdId: number | null = isEdit ? editRiskId : null;
      try {
        const json = await res.json();
        createdId = json?.id ?? createdId;
      } catch {
        // some backends respond 201 with empty body — that's fine
      }
      if (!isEdit && attachToContractId && createdId) {
        try {
          await fetch(apiUrl(`contracts/${attachToContractId}/risk-objects`), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ riskObjectId: createdId }),
          });
        } catch (e) {
          console.error("attach risk to contract failed", e);
        }
      }
      return { id: createdId };
    },
    [location.state, stateKontaktId, attachToContractId, editRiskId],
  );

  const renderTilePicker = () => (
    <div className={s.card}>
      <div className={s.cardHead}>
        <div>
          <div className={s.cardEyebrow}>Risiko erfassen</div>
          <div className={s.cardTitle}>Welche Art von Risiko möchten Sie erfassen?</div>
        </div>
      </div>
      <div className={s.goldLine} />
      <div className={s.tileGrid}>
        {TILES.map((t) => {
          const Icon = t.Icon;
          const wide = !!t.subRisks && t.subRisks.length > 0;
          return (
            <button
              key={t.key}
              type="button"
              className={mergeClasses(s.tile, wide && s.tileWide)}
              disabled={t.comingSoon}
              onClick={() => !t.comingSoon && setSelected(t.key)}
              style={{ backgroundColor: t.color }}
              aria-label={t.label}
              title={t.hint && t.hint.includes("\n") ? t.hint : undefined}
            >
              {t.comingSoon && <span className={s.tileSoonBadge}>bald</span>}
              <div className={s.tileTopRow}>
                <div className={s.tileIconWrap}>
                  <Icon />
                </div>
              </div>
              <div className={s.tileBody}>
                <div className={s.tileLabel}>{t.label}</div>
                {t.hint && !t.hint.includes("\n") && (
                  <div className={s.tileHint}>{t.hint}</div>
                )}
              </div>
              {t.subRisks && t.subRisks.length > 0 && (
                <div className={s.subRiskFooter}>
                  {t.subRisks.map((label) => (
                    <span key={label} className={s.subRiskBox}>
                      {label}
                    </span>
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  const tile = selected ? TILES.find((t) => t.key === selected) : null;

  // Form titles for risk types that are NOT shown as top-level tiles
  // (reachable only via the dialog's sub-risk boxes).
  const SUB_RISK_TITLES: Partial<Record<RiskKey, string>> = {
    machine: "Maschinen",
    waren: "Waren und Vorräte",
    elektronik: "Elektronik",
    betriebSonstige: "Sonstige (Betriebs- und Anlagevermögen)",
  };

  // Suffix-Berechnung für den Window-Titel — KFZ: Kennzeichen, Immobilie: Adresse.
  // Live-Update kommt vom Step-Form via onAnswersChange; wir reichen den
  // resultierenden Suffix an embedded.onTitleSuffix (Window-Adapter) weiter.
  const handleKfzAnswersChange = React.useCallback(
    (answers: Record<string, unknown>) => {
      // Dirty-Signal VOR dem onTitleSuffix-Guard — sonst bliebe es aus, sobald
      // kein Titel-Callback gesetzt ist.
      embedded?.onDirtyChange?.(true);
      if (!embedded?.onTitleSuffix) return;
      const plate = typeof answers.licensePlate === "string" ? answers.licensePlate.trim() : "";
      embedded.onTitleSuffix(plate || null);
    },
    [embedded],
  );

  const handleImmoAnswersChange = React.useCallback(
    ({ global }: { global: Record<string, unknown>; gebaeude: unknown[] }) => {
      embedded?.onDirtyChange?.(true);
      if (!embedded?.onTitleSuffix) return;
      const street = typeof global.street === "string" ? global.street.trim() : "";
      const city = typeof global.city === "string" ? global.city.trim() : "";
      const suffix = [street, city].filter(Boolean).join(", ");
      embedded.onTitleSuffix(suffix || null);
    },
    [embedded],
  );

  // Übrige Risiko-Typen: kein Titel-Suffix, aber dasselbe Dirty-Signal.
  const handleAnswersDirty = React.useCallback(() => {
    embedded?.onDirtyChange?.(true);
  }, [embedded]);

  const renderForm = () => {
    if (!selected) return null;
    // Edit-Mode: warten bis das Risiko geladen ist, damit die Step-Form mit
    // den richtigen Initial-Answers mountet (initialAnswers ist nur initial,
    // nachträgliche Änderungen würden ignoriert).
    if (editRiskId && editLoading) {
      return (
        <div className={s.formContainer} style={{ padding: 32 }}>
          Lade Risiko-Daten…
        </div>
      );
    }
    if (editRiskId && editError) {
      return (
        <div className={s.formContainer} style={{ padding: 32, color: "#a1331c" }}>
          {editError}
        </div>
      );
    }
    return (
      // Die beiden Sonderzustände oben (Laden / Fehler im Edit-Modus) sind hier
      // schon abgehandelt — `selected === "immobilie"` heißt an dieser Stelle
      // also: das Immobilien-Formular ist wirklich sichtbar.
      <div
        className={mergeClasses(
          s.formContainer,
          selected === "immobilie" && s.formContainerVollflaeche,
        )}
      >
        {selected === "kfz" && (
          <RisikoKfzStepForm
            chrome="embedded"
            initialAnswers={kfzInitialAnswers ?? undefined}
            embeddedSubmitLabel={
              editRiskId
                ? "Änderungen speichern"
                : attachToContractId
                  ? "KFZ speichern & zum Vertrag"
                  : "KFZ speichern"
            }
            onAnswersChange={handleKfzAnswersChange}
            onSubmitEmbedded={async (answers) => {
              try {
                await saveKfzRiskObject(answers as Record<string, any>);
                if (attachToContractId) {
                  showToast("success", "KFZ gespeichert und zum Vertrag hinzugefügt");
                  // Eingebettet läuft auch dieser Zweig über navigateAfterSave
                  // → onSuccess: der Adapter löscht das Dirty-Flag, schließt die
                  // Instanz und navigiert zum Vertrag. Ohne das blieb die
                  // Vollseite nach dem Speichern offen und „schmutzig".
                  if (isEmbedded) {
                    navigateAfterSave();
                  } else {
                    setTimeout(() => {
                      navigate(`/vertraege/edit/${attachToContractId}`);
                    }, 600);
                  }
                } else {
                  showToast("success", "KFZ gespeichert");
                  navigateAfterSave();
                }
              } catch {
                showToast("error", "KFZ konnte nicht gespeichert werden");
              }
            }}
          />
        )}
        {selected === "immobilie" && (
          <RisikoImmobilieStepForm
            chrome="embedded"
            // Kontextzeile statt Kopfzeile (19.08.2026): das Formular zeigt
            // „Immobilie · Anschrift — Kunde" selbst an und trägt dort auch die
            // drei Bedienelemente der Vollseite. `onCloseRequest` ist DERSELBE
            // Weg wie der frühere „Zurück"-Knopf — inklusive Rückfrage bei
            // ungespeicherten Eingaben.
            windowId={embedded?.windowId}
            kundenName={embedded?.kontaktName ?? null}
            onCloseRequest={handleClose}
            // Einziger authentifizierter Aufrufweg: hier — und nur hier — darf
            // die Beteiligten-Erfassung im Kontaktbestand suchen und Kontakte
            // anlegen.
            contactLookup
            initialGlobalAnswers={immoInitialGlobalAnswers ?? undefined}
            initialGebaeude={immoInitialGebaeude ?? undefined}
            embeddedSubmitLabel={editRiskId ? "Änderungen speichern" : "Immobilie speichern"}
            onAnswersChange={handleImmoAnswersChange}
            uploads={immoUploadMode}
            submitLocked={immoGespeichert}
            onSubmitEmbedded={async ({ global, gebaeude, uploads }) => {
              try {
                // stateKontaktId/attachToContractId berücksichtigen den embedded-
                // (Fenster-)Modus, in dem location.state leer ist — sonst fiele der
                // Kontakt-/Vertragsbezug auf null (verwaistes Risiko).
                const kontaktId = stateKontaktId;
                const vertragId =
                  attachToContractId ??
                  ((): number | null => {
                    const n = Number(location.state?.vertragId);
                    return Number.isFinite(n) && n > 0 ? n : null;
                  })();
                const isEdit = Boolean(editRiskId);
                if (isEdit) {
                  // Im Edit-Mode: nur das erste Gebäude (= das bestehende Risiko)
                  // wird per PUT aktualisiert. Multi-Gebäude-Erstellung ist
                  // nicht Teil des Edit-Pfads.
                  const g = gebaeude[0];
                  const merged: Record<string, unknown> = {
                    ...global,
                    ...g.answers,
                    gebaeudeLabel: g.label,
                  };
                  const characteristics = Object.entries(merged)
                    .filter(
                      ([, value]) =>
                        value !== undefined &&
                        value !== null &&
                        (typeof value !== "string" || value.trim() !== "") &&
                        !(
                          typeof value === "object" &&
                          Object.keys(value as object).length === 0
                        ),
                    )
                    .map(([nameBezeichnung, value]) => ({
                      nameBezeichnung,
                      riskCharacteristicValue:
                        typeof value === "string" || typeof value === "number"
                          ? String(value)
                          : JSON.stringify(value),
                    }));
                  const typ = String(g.answers.typ ?? g.answers.kategorie ?? "Immobilie");
                  const adressTeil = [
                    global.street,
                    [global.postalCode, global.city].filter(Boolean).join(" "),
                  ]
                    .filter(Boolean)
                    .join(", ");
                  const name = adressTeil ? `${typ}: ${adressTeil}` : `${typ} (${g.label})`;
                  const res = await fetch(apiUrl(`risk-objects/${editRiskId}`), {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      contactID: kontaktId,
                      riskType: "Immobilie",
                      name,
                      characteristics,
                    }),
                  });
                  if (!res.ok) throw new Error(await res.text());
                  immoGespeichertMerken();
                  // PV-Anlage/Öltank als Kind-Risiken nachziehen (Punkt J):
                  // vorhandene Kinder gleicher Art werden aktualisiert, nicht
                  // doppelt angelegt; entfallene bleiben stehen.
                  const unterOk = await (async () => {
                    try {
                      const r = await fetch(apiUrl(`risk-objects/${editRiskId}`));
                      if (!r.ok) throw new Error(await r.text());
                      const aktuell = await r.json();
                      await speichereUnterRisiken({
                        adresse: global,
                        gebaeude: [
                          {
                            riskObjectId: editRiskId as number,
                            answers: g.answers,
                            gebaeudeLabel: g.label,
                            kinder: Array.isArray(aktuell?.children) ? aktuell.children : [],
                          },
                        ],
                        contactID: kontaktId,
                        contractID: Number(aktuell?.contract?.id ?? aktuell?.contractID) || null,
                      });
                      return true;
                    } catch {
                      return false;
                    }
                  })();
                  showToast(
                    unterOk ? "success" : "error",
                    unterOk
                      ? "Immobilie aktualisiert"
                      : "Immobilie aktualisiert – PV-Anlage/Öltank konnten nicht gespeichert werden",
                  );
                  navigateAfterSave();
                  return;
                }
                // Alle Gebäude in EINER Transaktion anlegen (Bulk) — kein
                // Teil-Speichern/Duplikate bei Abbruch (POST /risk-objects/bulk).
                const items = gebaeude.map((g) => {
                  const merged: Record<string, unknown> = {
                    ...global,
                    ...g.answers,
                    gebaeudeLabel: g.label,
                  };
                  const characteristics = Object.entries(merged)
                    .filter(
                      ([, value]) =>
                        value !== undefined &&
                        value !== null &&
                        (typeof value !== "string" || value.trim() !== "") &&
                        !(
                          typeof value === "object" &&
                          Object.keys(value as object).length === 0
                        ),
                    )
                    .map(([nameBezeichnung, value]) => ({
                      nameBezeichnung,
                      riskCharacteristicValue:
                        typeof value === "string" || typeof value === "number"
                          ? String(value)
                          : JSON.stringify(value),
                    }));
                  const typ = String(g.answers.typ ?? g.answers.kategorie ?? "Immobilie");
                  const adressTeil = [
                    global.street,
                    [global.postalCode, global.city].filter(Boolean).join(" "),
                  ]
                    .filter(Boolean)
                    .join(", ");
                  const name = adressTeil
                    ? `${typ}: ${adressTeil}`
                    : `${typ} (${g.label})`;
                  return {
                    contractID: vertragId,
                    contactID: kontaktId,
                    riskType: "Immobilie",
                    name,
                    characteristics,
                  };
                });
                const res = await fetch(apiUrl("risk-objects/bulk"), {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ items }),
                });
                if (!res.ok) throw new Error(await res.text());
                // Die Antwort trägt die frisch vergebenen Ids — in der
                // Reihenfolge der items. Erst damit haben die vorgemerkten
                // Dateien ein Ziel.
                const created: Array<{ id?: number }> = await res
                  .json()
                  .catch(() => [] as Array<{ id?: number }>);
                const createdIds = (Array.isArray(created) ? created : [])
                  .map((r) => Number(r?.id))
                  .filter((n) => Number.isFinite(n) && n > 0);
                immoGespeichertMerken();
                // PV-Anlage/Öltank je Gebäude als Kind-Risiken anlegen (Punkt J).
                // Nur bei eindeutiger Zuordnung Gebäude ↔ Id (Bulk liefert in
                // items-Reihenfolge).
                let unterOk = true;
                if (createdIds.length === gebaeude.length) {
                  try {
                    await speichereUnterRisiken({
                      adresse: global,
                      gebaeude: gebaeude.map((g, i) => ({
                        riskObjectId: createdIds[i],
                        answers: g.answers,
                        gebaeudeLabel: g.label,
                      })),
                      contactID: kontaktId,
                      contractID: vertragId,
                    });
                  } catch {
                    unterOk = false;
                  }
                } else if (
                  gebaeude.some((g) => baueUnterRisiken(global, g.answers, g.label).length > 0)
                ) {
                  unterOk = false;
                }
                const gespeichertText =
                  gebaeude.length === 1
                    ? "Immobilie gespeichert"
                    : `${gebaeude.length} Gebäude gespeichert`;
                showToast(
                  unterOk ? "success" : "error",
                  unterOk
                    ? gespeichertText
                    : `${gespeichertText} – PV-Anlage/Öltank konnten nicht angelegt werden`,
                );

                const vorgemerkt = uploads ?? [];
                if (vorgemerkt.length === 0) {
                  navigateAfterSave();
                  return;
                }
                if (createdIds.length === 0) {
                  // Gespeichert, aber ohne verwertbare Ids — die Dateien haben
                  // kein Ziel. Stilles Schließen verlöre sie unwiederbringlich
                  // (`File` ist nicht serialisierbar), also stehenbleiben und
                  // sagen, was los ist.
                  setUploadProblem({
                    fehler: vorgemerkt.map((p) => ({
                      label: p.label,
                      fileName: p.file.name,
                      grund: "Die Risikoobjekte konnten nicht zugeordnet werden.",
                    })),
                    erfolgreich: 0,
                    riskObjectId: null,
                  });
                  return;
                }

                // Nachlauf: streng sequenziell, mit 429-Backoff. Erst danach
                // wird geschlossen — und bei Teilfehlern gar nicht.
                setUploadFortschritt({ erledigt: 0, gesamt: vorgemerkt.length });
                let ergebnis;
                try {
                  ergebnis = await ladeVorgemerkteHoch({
                    pending: vorgemerkt,
                    ziele: zieleFuerGebaeude(
                      gebaeude.map((g) => g.id),
                      createdIds,
                    ),
                    // Slots der Objektebene (`GLOBAL_SCOPE`, z. B. das
                    // Objektfoto) hängen am ersten Gebäude.
                    fallbackRiskObjectId: createdIds[0],
                    contactId: kontaktId,
                    onFortschritt: (erledigt, gesamt) =>
                      setUploadFortschritt({ erledigt, gesamt }),
                  });
                } catch (err) {
                  // Der Nachlauf fängt je Datei ab; kommt hier trotzdem etwas
                  // an, ist das KEIN Speicherfehler — das Risikoobjekt steht
                  // bereits. Die äußere Meldung „konnte nicht gespeichert
                  // werden" wäre schlicht falsch.
                  ergebnis = {
                    erfolgreich: 0,
                    fehler: vorgemerkt.map((p) => ({
                      label: p.label,
                      fileName: p.file.name,
                      grund: err instanceof Error ? err.message : "Upload fehlgeschlagen.",
                    })),
                  };
                }
                setUploadFortschritt(null);

                if (ergebnis.fehler.length > 0) {
                  setUploadProblem({
                    fehler: ergebnis.fehler,
                    erfolgreich: ergebnis.erfolgreich,
                    riskObjectId: createdIds[0],
                  });
                  return; // Fenster bleibt offen, der Hinweis bleibt lesbar.
                }
                navigateAfterSave();
              } catch {
                setUploadFortschritt(null);
                showToast("error", "Immobilie konnte nicht gespeichert werden");
              }
            }}
          />
        )}
        {(selected === "betriebsvermoegen" ||
          selected === "waren" ||
          selected === "elektronik" ||
          selected === "betriebSonstige") && (
          <BusinessAssetsDialog
            inline
            open
            value={businessAssetsRisk}
            onSave={(next) => {
              setBusinessAssetsRisk(next);
              showToast("success", "Betriebsvermögen gespeichert");
              navigateAfterSave();
            }}
            onClose={() => setSelected(null)}
          />
        )}
        {selected === "machine" && (
          <RisikoMaschinenStepForm
            chrome="embedded"
            embeddedSubmitLabel="Maschine speichern"
            onAnswersChange={handleAnswersDirty}
            onSubmitEmbedded={async (answers) => {
              try {
                const kontaktId = stateKontaktId;
                const characteristics = Object.entries(answers)
                  .filter(
                    ([, value]) =>
                      value !== undefined &&
                      value !== null &&
                      String(value).trim() !== "",
                  )
                  .map(([nameBezeichnung, value]) => ({
                    nameBezeichnung,
                    riskCharacteristicValue: String(value),
                  }));
                const ort =
                  (typeof answers.machineDescription === "string" &&
                    answers.machineDescription.trim()) ||
                  (typeof answers.manufacturer === "string" &&
                    answers.manufacturer.trim()) ||
                  "Maschine";
                const res = await fetch(apiUrl("risk-objects"), {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    contractID: null,
                    contactID: kontaktId,
                    riskType: "Maschine",
                    name: ort,
                    characteristics,
                  }),
                });
                if (!res.ok) throw new Error(await res.text());
                showToast("success", "Maschine gespeichert");
                navigateAfterSave();
              } catch {
                showToast("error", "Maschine konnte nicht gespeichert werden");
              }
            }}
          />
        )}
        {selected === "liquiditaet" && (
          <RisikoLiquiditaetStepForm
            chrome="embedded"
            schema={liquiditaetSchema}
            embeddedSubmitLabel="Liquidität speichern"
            onAnswersChange={handleAnswersDirty}
            onSubmitEmbedded={async (answers) => {
              try {
                const kontaktId = stateKontaktId;
                const characteristics = Object.entries(answers)
                  .filter(
                    ([, value]) =>
                      value !== undefined &&
                      value !== null &&
                      String(value).trim() !== "",
                  )
                  .map(([nameBezeichnung, value]) => ({
                    nameBezeichnung,
                    riskCharacteristicValue: String(value),
                  }));
                const res = await fetch(apiUrl("risk-objects"), {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    contractID: null,
                    contactID: kontaktId,
                    riskType: "Liquidität",
                    name: "Liquidität",
                    characteristics,
                  }),
                });
                if (!res.ok) throw new Error(await res.text());
                showToast("success", "Liquidität gespeichert");
                navigateAfterSave();
              } catch {
                showToast("error", "Liquidität konnte nicht gespeichert werden");
              }
            }}
          />
        )}
        {selected === "cyber" && (
          <RisikoLiquiditaetStepForm
            chrome="embedded"
            schema={cyberSchema}
            embeddedSubmitLabel="Cyber-Risiko speichern"
            onAnswersChange={handleAnswersDirty}
            onSubmitEmbedded={async (answers) => {
              try {
                const kontaktId = stateKontaktId;
                const characteristics = Object.entries(answers)
                  .filter(
                    ([, value]) =>
                      value !== undefined &&
                      value !== null &&
                      (typeof value !== "string" || value.trim() !== "") &&
                      !(Array.isArray(value) && value.length === 0),
                  )
                  .map(([nameBezeichnung, value]) => ({
                    nameBezeichnung,
                    riskCharacteristicValue: Array.isArray(value)
                      ? value.join(",")
                      : String(value),
                  }));
                const res = await fetch(apiUrl("risk-objects"), {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    contractID: null,
                    contactID: kontaktId,
                    riskType: "Cyber",
                    name: "Cyber-Risiko",
                    characteristics,
                  }),
                });
                if (!res.ok) throw new Error(await res.text());
                showToast("success", "Cyber-Risiko gespeichert");
                navigateAfterSave();
              } catch {
                showToast("error", "Cyber-Risiko konnte nicht gespeichert werden");
              }
            }}
          />
        )}
        {selected === "ertragsausfall" && (
          <RisikoLiquiditaetStepForm
            chrome="embedded"
            schema={ertragsausfallSchema}
            embeddedSubmitLabel="Ertragsausfall speichern"
            onAnswersChange={handleAnswersDirty}
            onSubmitEmbedded={async (answers) => {
              try {
                const kontaktId = stateKontaktId;
                const characteristics = Object.entries(answers)
                  .filter(
                    ([, value]) =>
                      value !== undefined &&
                      value !== null &&
                      String(value).trim() !== "",
                  )
                  .map(([nameBezeichnung, value]) => ({
                    nameBezeichnung,
                    riskCharacteristicValue: String(value),
                  }));
                const res = await fetch(apiUrl("risk-objects"), {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    contractID: null,
                    contactID: kontaktId,
                    riskType: "Ertragsausfall",
                    name: "Ertragsausfall",
                    characteristics,
                  }),
                });
                if (!res.ok) throw new Error(await res.text());
                showToast("success", "Ertragsausfall gespeichert");
                navigateAfterSave();
              } catch {
                showToast(
                  "error",
                  "Ertragsausfall konnte nicht gespeichert werden",
                );
              }
            }}
          />
        )}
        {selected === "umwelt" && (
          <RisikoUmweltStepForm
            chrome="embedded"
            embeddedSubmitLabel="Umwelt-Risiko speichern"
            onAnswersChange={handleAnswersDirty}
            onSubmitEmbedded={async (answers) => {
              try {
                const kontaktId = stateKontaktId;
                const characteristics = Object.entries(answers)
                  .filter(
                    ([, value]) =>
                      value !== undefined &&
                      value !== null &&
                      String(value).trim() !== "",
                  )
                  .map(([nameBezeichnung, value]) => ({
                    nameBezeichnung,
                    riskCharacteristicValue: String(value),
                  }));
                const res = await fetch(apiUrl("risk-objects"), {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    contractID: null,
                    contactID: kontaktId,
                    riskType: "Umwelt",
                    name: "Umwelt-Risiko",
                    characteristics,
                  }),
                });
                if (!res.ok) throw new Error(await res.text());
                showToast("success", "Umwelt-Risiko gespeichert");
                navigateAfterSave();
              } catch {
                showToast("error", "Umwelt-Risiko konnte nicht gespeichert werden");
              }
            }}
          />
        )}
        {selected === "transport" && (
          <RisikoTransportStepForm
            chrome="embedded"
            embeddedSubmitLabel="Transport-Risiko speichern"
            onAnswersChange={handleAnswersDirty}
            onSubmitEmbedded={async (answers) => {
              try {
                const kontaktId = stateKontaktId;
                const characteristics = Object.entries(answers)
                  .filter(
                    ([, value]) =>
                      value !== undefined &&
                      value !== null &&
                      String(value).trim() !== "",
                  )
                  .map(([nameBezeichnung, value]) => ({
                    nameBezeichnung,
                    riskCharacteristicValue: String(value),
                  }));
                const res = await fetch(apiUrl("risk-objects"), {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    contractID: null,
                    contactID: kontaktId,
                    riskType: "Transport",
                    name: "Transport, Messen & Reisegepäck",
                    characteristics,
                  }),
                });
                if (!res.ok) throw new Error(await res.text());
                showToast("success", "Transport-Risiko gespeichert");
                navigateAfterSave();
              } catch {
                showToast(
                  "error",
                  "Transport-Risiko konnte nicht gespeichert werden",
                );
              }
            }}
          />
        )}
      </div>
    );
  };

  // Solange der Picker-Dialog offen ist und noch nichts gewählt wurde, bleibt
  // der Body leer — hinter dem Dialog gäbe es sonst nur die Kachelauswahl
  // doppelt. Nach „Zurück"/Schließen erscheint die Risiken-Tabelle, die die
  // Route (NewRiskRoute) unter der Vollseite bereithält.
  const pickerPending = pickerDialogOpen && !selected;

  /**
   * Das Immobilien-Formular bringt seine Bedienung selbst mit: es zeigt über
   * der Abschnittsüberschrift eine Kontextzeile („Immobilie · Anschrift —
   * Kunde") und rechts darin „Als Fenster", „Minimieren" und „Schließen"
   * (Auftraggeber, 19.08.2026). Eine zusätzliche Kopfzeile wäre dort eine
   * zweite Überschrift und eine doppelte Bedienung.
   *
   * ALLE ÜBRIGEN Risikoarten (KFZ, Maschinen, Liquidität, Umwelt, Transport,
   * Kennzahlen, Kachel-Picker) behalten die Kopfzeile: sie haben keine
   * Kontextzeile, in die die Knöpfe einziehen könnten — und im Vollseiten-Modus
   * blendet FloatingWindow die Titelleiste aus. Ohne Kopfzeile verlören sie
   * also ihre EINZIGE Schließen-Möglichkeit.
   *
   * Festgemacht am gerenderten Formular, nicht am blossen `riskType`: bei
   * `editRiskId` steht `selected` schon vor dem Laden auf "immobilie", während
   * `renderForm` nur „Lade Risiko-Daten…" bzw. die Fehlermeldung zeigt — dort
   * gäbe es sonst weder Zurück noch Schließen.
   */
  const immoFormularSichtbar =
    selected === "immobilie" && !(editRiskId && (editLoading || editError));

  return (
    <div className={mergeClasses(s.page, immoFormularSichtbar && s.pageVollflaeche)}>
      {/* Die Kopfzeile wird ansonsten IMMER gerendert. Früher entfiel sie im
          Picker-Zustand — zusammen mit der fehlenden Titelleiste im
          Vollseiten-Modus gäbe es dann weder „Zurück" noch Bedienelemente. */}
      {!immoFormularSichtbar && (
        <div className={mergeClasses(s.topRow, isPageChrome && s.topRowSticky)}>
          <button className={s.ghostBtn} onClick={handleClose}>
            <ArrowLeft20Regular /> Zurück
          </button>
          <div className={s.title}>
            {selected
              ? `Risiko erfassen — ${tile?.label ?? SUB_RISK_TITLES[selected] ?? "Risiko"}`
              : "Risiko erfassen"}
          </div>
          <div className={s.topRowSpacer} />
          <PageModeControls windowId={embedded?.windowId} />
        </div>
      )}

      {pickerPending ? null : !selected ? renderTilePicker() : renderForm()}

      <NewRiskDialog
        open={pickerDialogOpen}
        onClose={() => {
          setPickerDialogOpen(false);
          // Ohne Auswahl gibt es hier nichts mehr zu tun. Im eingebetteten
          // Modus NICHT navigieren — ein Routenwechsel würde bei jeder Typwahl
          // den WindowNavigationGuard auslösen; stattdessen schließen.
          if (selected) return;
          if (isEmbedded) embedded?.onClose?.();
          else navigate("/risks");
        }}
        onPicked={embedded?.onReplace}
        contactId={stateKontaktId ?? undefined}
        initialType={stateRiskType ?? undefined}
      />

      {toast && (
        <div
          className={mergeClasses(
            s.toast,
            toast.kind === "success" ? s.toastSuccess : s.toastError,
          )}
          role="status"
        >
          {toast.message}
        </div>
      )}

      {uploadFortschritt && (
        <div className={mergeClasses(s.uploadFortschritt)} role="status">
          {`Dateien werden hochgeladen … ${uploadFortschritt.erledigt}/${uploadFortschritt.gesamt}`}
        </div>
      )}

      {uploadProblem && (
        <div className={mergeClasses(s.uploadHinweis)} role="alert">
          <span className={mergeClasses(s.uploadHinweisTitel)}>
            {uploadProblem.erfolgreich > 0
              ? `Die Immobilie ist gespeichert, ${uploadProblem.erfolgreich} von ${uploadProblem.erfolgreich + uploadProblem.fehler.length} Dateien wurden hochgeladen.`
              : "Die Immobilie ist gespeichert, die Dateien konnten nicht hochgeladen werden."}
          </span>
          <ul className={mergeClasses(s.uploadHinweisListe)}>
            {uploadProblem.fehler.map((f, i) => (
              <li key={i}>{`${f.label}: ${f.fileName} — ${f.grund}`}</li>
            ))}
          </ul>
          <span className={mergeClasses(s.uploadHinweisListe)}>
            Die fehlenden Dateien lassen sich auf der Risiko-Detailseite unter
            „Dokumente &amp; Fotos" nachreichen.
          </span>
          <div className={mergeClasses(s.uploadHinweisZeile)}>
            <button
              type="button"
              className={mergeClasses(s.uploadHinweisBtn, s.uploadHinweisBtnGhost)}
              onClick={() => {
                setUploadProblem(null);
                navigateAfterSave();
              }}
            >
              Schließen
            </button>
            {uploadProblem.riskObjectId != null && (
              <button
                type="button"
                className={mergeClasses(s.uploadHinweisBtn)}
                onClick={() => {
                  const ziel = `/risks/${uploadProblem.riskObjectId}`;
                  setUploadProblem(null);
                  // Eingebettet würde ein Routenwechsel den
                  // WindowNavigationGuard auslösen; dort erst schließen.
                  if (isEmbedded) embedded?.onSuccess?.();
                  navigate(ziel);
                }}
              >
                Zur Risiko-Detailseite
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewRiskPage;
